import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { apiError, useToast } from './Toast';
import { Trash2, RotateCcw, Archive, X, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * frontend/src/components/BulkActions.jsx
 *
 * Selecting many records and acting on them, built once for seven list pages.
 *
 * Three pieces:
 *
 *   useSelection(rows)   which rows are ticked, and the select-all state
 *   <BulkBar/>           the bar that appears when something is ticked
 *   <TypeToConfirm/>     the dialog for an action that cannot be undone
 *
 * Two rules the whole thing exists to hold:
 *
 *   Nothing happens before the person has seen what would happen. Every
 *   action asks the server for a preview first, and the dialog is built from
 *   the answer. An administrator should never confirm a number they have not
 *   been shown.
 *
 *   Selecting a row grants nothing. The server decides, per row, exactly as it
 *   does for a single delete, and names what it refused. The bar reports that
 *   back rather than hiding it.
 */

const SAPPHIRE = '#2E539F';
const SUNGLOW  = '#FFC849';
const DANGER   = '#ef4444';

/* ── selection ──────────────────────────────────────────────────────────── */

/**
 * Tracks ticked ids against the rows currently on screen.
 *
 * Filtering a list must not silently keep a hidden row ticked: deleting
 * something you cannot see is the worst kind of surprise. `selected` is
 * therefore always intersected with what is visible, while `raw` keeps the
 * ticks so they survive a filter being switched off and on again.
 */
export function useSelection(rows) {
  const [raw, setRaw] = useState(() => new Set());
  const visible = useMemo(() => new Set((rows || []).map((r) => r.id)), [rows]);

  const selected = useMemo(
    () => (rows || []).filter((r) => raw.has(r.id)).map((r) => r.id),
    [rows, raw]);

  const toggle = useCallback((id) => setRaw((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  }), []);

  const toggleAll = useCallback(() => setRaw((s) => {
    const allOn = [...visible].every((id) => s.has(id)) && visible.size > 0;
    const n = new Set(s);
    visible.forEach((id) => (allOn ? n.delete(id) : n.add(id)));
    return n;
  }), [visible]);

  const clear = useCallback(() => setRaw(new Set()), []);

  const allChecked  = visible.size > 0 && selected.length === visible.size;
  const someChecked = selected.length > 0 && !allChecked;

  return { selected, isSelected: (id) => raw.has(id), toggle, toggleAll, clear,
           allChecked, someChecked, count: selected.length };
}

/** The header checkbox, including the dash that means "some of them". */
export function SelectAll({ allChecked, someChecked, onToggle, disabled }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!someChecked; }, [someChecked]);
  return (
    <input ref={ref} type="checkbox" checked={allChecked} onChange={onToggle}
      disabled={disabled} aria-label="Select all on this page"
      className="cursor-pointer" style={{ accentColor: SAPPHIRE, width: 15, height: 15 }} />
  );
}

/** One row's checkbox. Stops the click reaching the row behind it. */
export function RowCheck({ checked, onToggle, label }) {
  return (
    <input type="checkbox" checked={checked}
      onChange={onToggle} onClick={(e) => e.stopPropagation()}
      aria-label={label ? `Select ${label}` : 'Select this record'}
      className="cursor-pointer" style={{ accentColor: SAPPHIRE, width: 15, height: 15 }} />
  );
}

/* ── type-to-confirm ────────────────────────────────────────────────────── */

/**
 * For the actions a click should not be enough for.
 *
 * A dialog can be clicked through on reflex. A word that has to be typed
 * cannot, and the typing is the pause in which people notice the number.
 */
export function TypeToConfirm({ open, word, title, body, lines, confirmLabel,
                               onConfirm, onCancel, busy, danger }) {
  const [typed, setTyped] = useState('');
  useEffect(() => { if (open) setTyped(''); }, [open]);
  if (!open) return null;

  // No word, no box. Restore asks for a click and nothing more; undoing your
  // own mistake should not have a hurdle in front of it.
  const needsWord = !!word;
  const ready = !needsWord || typed.trim() === word;

  return createPortal(
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => !busy && onCancel()} />
      <div className="relative w-full max-w-lg rounded-xl shadow-2xl p-5"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <div className="flex gap-3 mb-4">
          <span className="flex-shrink-0 mt-0.5" style={{ color: danger === false ? SAPPHIRE : DANGER }}>
            <AlertTriangle size={20} />
          </span>
          <div className="min-w-0">
            <p className="font-semibold mb-1" style={{ color: 'var(--text)' }}>{title}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{body}</p>
          </div>
        </div>

        {!!lines?.length && (
          <ul className="text-sm mb-4 rounded-lg px-3 py-2 space-y-1"
            style={{ background: 'var(--surface-alt)', color: 'var(--text)' }}>
            {lines.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        )}

        {needsWord && (
          <>
            <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              Type <strong style={{ color: 'var(--text)' }}>{word}</strong> to confirm
            </label>
            <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && ready && !busy) onConfirm(); }}
              className="input w-full mb-4" placeholder={word} spellCheck={false} />
          </>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="btn-secondary text-sm">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={!ready || busy} autoFocus={!needsWord}
            className="text-sm px-3.5 py-2 rounded-lg font-semibold text-white flex items-center gap-1.5"
            style={{ background: !ready || busy ? '#cbd5e1' : (danger === false ? SAPPHIRE : DANGER),
                     cursor: ready && !busy ? 'pointer' : 'not-allowed' }}>
            {busy && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body);
}

/* ── the bar ────────────────────────────────────────────────────────────── */

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Appears when something is ticked, and only then.
 *
 * `bulk` is the four-call object from api.js: tasksAPI.bulk and friends.
 * `view` decides which actions make sense:
 *
 *   live     Delete        stage 1
 *   deleted  Restore, Clear from Deleted
 *
 * `onDone` reloads the page's own data. The bar does not know how.
 */
export function BulkBar({ bulk, view = 'live', selected, label, labelPlural,
                          onDone, onClear, canDiscard = true }) {
  const toast = useToast();
  const [pending, setPending] = useState(null);   // { action, preview }
  const [busy, setBusy] = useState(false);

  if (!selected.length) return null;

  const ACTIONS = {
    delete:  { verb: 'Delete',  word: 'DELETE',  danger: true,
               stage: 'delete',  call: bulk.delete,
               title: `Delete ${plural(selected.length, label, labelPlural)}?`,
               body: 'They leave the lists and move to the Deleted view, where they can be restored.' },
    restore: { verb: 'Restore', word: null,      danger: false,
               stage: 'restore', call: bulk.restore,
               title: `Restore ${plural(selected.length, label, labelPlural)}?`,
               body: 'They come back to the lists, with whatever was deleted alongside them.' },
    discard: { verb: 'Clear from Deleted', word: 'CLEAR', danger: true,
               stage: 'discard', call: bulk.discard,
               title: `Clear ${plural(selected.length, label, labelPlural)} from your Deleted view?`,
               body: 'They move to the administrator’s recycle bin. Nothing is removed from the database, and an administrator can still restore them.' },
  };

  const start = async (key) => {
    if (selected.length > 500) {
      toast.error(`That is ${selected.length} records. 500 is the most in one go.`);
      return;
    }
    setBusy(true);
    try {
      const r = await bulk.preview(selected, ACTIONS[key].stage);
      const p = r.data || {};
      if (!p.allowed) {
        // Three different reasons, and lumping them together as "nothing to
        // act on" sent people looking for a fault that was not there.
        toast.error(
          p.denied?.length  ? `None of those ${labelPlural} are yours to ${ACTIONS[key].verb.toLowerCase()}.`
        : p.skipped         ? `Those ${labelPlural} are already in that state.`
        :                     `Nothing there to act on.`);
        return;
      }
      setPending({ key, preview: p });
    } catch (e) {
      toast.error(apiError(e, 'Could not work out what that would do.'));
    } finally { setBusy(false); }
  };

  const run = async () => {
    const a = ACTIONS[pending.key];
    setBusy(true);
    try {
      const r = await a.call(selected);
      toast.success(r.data?.message || 'Done');
      const denied = r.data?.denied || [];
      if (denied.length) {
        toast.warning(denied.length === 1
          ? `${denied[0]} was not yours to change, so it was left alone.`
          : `${denied.length} records were not yours to change and were left alone.`);
      }
      setPending(null);
      onClear?.();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, 'Nothing was changed.'));
    } finally { setBusy(false); }
  };

  const a = pending ? ACTIONS[pending.key] : null;
  const p = pending?.preview;

  const lines = p ? [
    `${plural(p.allowed, label, labelPlural)} will change.`,
    p.cascade_text ? `${p.cascade_text} will go with them.` : null,
    p.denied?.length ? `${plural(p.denied.length, 'record', 'records')} are not yours and will be left alone.` : null,
    p.skipped ? `${plural(p.skipped, 'record is', 'records are')} already in that state and will be skipped.` : null,
  ].filter(Boolean) : [];

  return (
    <>
      <div className="fixed left-1/2 -translate-x-1/2 z-[150] flex items-center gap-2 px-3 py-2 rounded-xl shadow-lg"
        style={{ bottom: 20, background: 'var(--surface)', border: '1px solid var(--border)',
                 borderTop: `3px solid ${SAPPHIRE}` }}>
        <span className="text-sm font-semibold px-1" style={{ color: 'var(--text)' }}>
          {plural(selected.length, 'selected', 'selected')}
        </span>

        {view === 'live' && (
          <button onClick={() => start('delete')} disabled={busy}
            className="text-sm px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5"
            style={{ background: 'rgba(239,68,68,.10)', color: DANGER }}>
            <Trash2 size={14} /> Delete
          </button>
        )}

        {view === 'deleted' && (
          <>
            <button onClick={() => start('restore')} disabled={busy}
              className="text-sm px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5"
              style={{ background: 'rgba(46,83,159,.10)', color: SAPPHIRE }}>
              <RotateCcw size={14} /> Restore
            </button>
            {canDiscard && (
              <button onClick={() => start('discard')} disabled={busy}
                className="text-sm px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5"
                style={{ background: 'rgba(255,200,73,.18)', color: '#92700f' }}>
                <Archive size={14} /> Clear from Deleted
              </button>
            )}
          </>
        )}

        <button onClick={onClear} aria-label="Clear the selection"
          className="p-1.5 rounded-lg" style={{ color: 'var(--text-secondary)' }}>
          <X size={15} />
        </button>
      </div>

      {/* Restore passes no word, so the dialog is a plain confirmation:
          undoing your own mistake should not have a hurdle in front of it. */}
      {pending && (
        <TypeToConfirm open word={a.word} danger={a.danger} title={a.title}
          body={a.body} lines={lines} confirmLabel={a.verb} busy={busy}
          onConfirm={run} onCancel={() => setPending(null)} />
      )}
    </>
  );
}
